import React, { useCallback, useEffect, useMemo, useState } from "react";

import { DashboardLayout } from "../../../components/layout";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import {
  deleteFreightAccountUnderReviewSliderImage,
  getFreightAccountUnderReviewSliderIndex,
  storeFreightAccountUnderReviewSliderImages
} from "../../../utils/api";
import { freightAccountUnderReviewSliderItems } from "../../../utils/freightAdminListResponse";
import { showError, showSuccess } from "../../../utils/toast";
import "./adminFreight.css";

const MAX_SLIDES = 30;
const MAX_BYTES = 8 * 1024 * 1024;
const FIELD_NAME = "freight_account_under_review_page_slider";
const ACCEPT_RE = /\.(jpe?g|png|gif)$/i;

function validateFiles(files, currentCount) {
  if (currentCount + files.length > MAX_SLIDES) {
    return `At most ${MAX_SLIDES} slides allowed (${currentCount} already on server).`;
  }
  for (let i = 0; i < files.length; i += 1) {
    const f = files[i];
    if (f.size > MAX_BYTES) return `"${f.name}" exceeds 8 MB.`;
    if (!ACCEPT_RE.test(f.name)) return `"${f.name}": only jpg, jpeg, png, or gif.`;
  }
  return null;
}

const FreightAccountUnderReviewSliderPage = () => {
  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = useMemo(
    () => [
      { label: roleLabel, link: "/dashboard" },
      { label: "Freight", link: "/freight" },
      { label: "Account under review slider" }
    ],
    [roleLabel]
  );

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFreightAccountUnderReviewSliderIndex();
      setItems(freightAccountUnderReviewSliderItems(res));
    } catch (e) {
      showError(e.message || "Failed to load slider");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onPickFiles = async (e) => {
    const raw = Array.from(e.target.files || []);
    e.target.value = "";
    if (raw.length === 0) return;
    const err = validateFiles(raw, items.length);
    if (err) {
      showError(err);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      raw.forEach((file) => fd.append(FIELD_NAME, file));
      const res = await storeFreightAccountUnderReviewSliderImages(fd);
      const n = res?.data?.count ?? res?.data?.saved?.length;
      showSuccess(n ? `Uploaded ${n} image(s).` : "Upload complete.");
      setFileInputKey((k) => k + 1);
      await load();
    } catch (ex) {
      showError(ex.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (file) => {
    const ok = window.confirm(`Delete "${file}" from the slider?`);
    if (!ok) return;
    try {
      const res = await deleteFreightAccountUnderReviewSliderImage(file);
      showSuccess(res?.message || "Image removed");
      await load();
    } catch (ex) {
      showError(ex.message || "Delete failed");
    }
  };

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="max-w-[1400px]">
        <div className="card offers-table border-l-4 border-l-[#064E8D]">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
            <h3 className="m-0">Account under review — page slider</h3>
            <button type="button" className="btn-outline text-sm" onClick={() => load()} disabled={loading}>
              Refresh
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-4">
            Up to <strong>{MAX_SLIDES}</strong> images, <strong>8 MB</strong> each, <strong>jpg / jpeg / png / gif</strong>.
            Shown on the freight “account under review” screen. Field name on upload:{" "}
            <code className="text-xs bg-gray-100 px-1 rounded">{FIELD_NAME}</code>.
          </p>

          <div className="freight-admin-form mb-6 max-w-xl">
            <label className="block text-sm font-medium text-gray-700 mb-1">Add slides</label>
            <input
              key={fileInputKey}
              type="file"
              accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif"
              multiple
              disabled={uploading || items.length >= MAX_SLIDES}
              onChange={onPickFiles}
              className="text-sm"
            />
            {uploading ? <p className="text-sm text-gray-600 mt-1 m-0">Uploading…</p> : null}
            {items.length >= MAX_SLIDES ? (
              <p className="text-sm text-amber-800 mt-1 m-0">Maximum number of slides reached. Delete one to add more.</p>
            ) : null}
          </div>

          {loading ? (
            <p className="text-gray-600">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-gray-600">No slider images yet. Use the file control above to upload.</p>
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 list-none m-0 p-0">
              {items.map((it) => (
                <li
                  key={it.file}
                  className="rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm flex flex-col"
                >
                  <div className="aspect-[16/9] bg-gray-100 flex items-center justify-center overflow-hidden">
                    {it.url ? (
                      <img src={it.url} alt="" className="max-h-full max-w-full object-contain" loading="lazy" />
                    ) : (
                      <span className="text-xs text-gray-500 px-2 text-center">Preview URL not returned — filename below</span>
                    )}
                  </div>
                  <div className="p-3 flex flex-col gap-2 flex-1">
                    <p className="text-xs text-gray-600 m-0 break-all font-mono" title={it.file}>
                      {it.file}
                    </p>
                    <button type="button" className="btn-outline text-sm self-start" onClick={() => onDelete(it.file)}>
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreightAccountUnderReviewSliderPage;
